import { v4 as uuidv4 } from 'uuid'
import { z } from 'zod'
import { validateJsonTextElement } from './jsonTextElementsSchema'
import dayjs from 'dayjs'
import { useAuthStore } from '@/stores/auth'

/**
 * ParagraphElement class
 * - Empty element
 * - Placeholder for user input
 */
class ParagraphElement {
  constructor() {
    this.tag = 'p'
    this.text = ''
    this.element = 'paragraph'
    this.id = 'paragraph_id'
  }
}

/**
 * BaseElement class
 * - Serves as the foundation for all element types.
 * - Provides shared properties such as time, task ID, and element type.
 */
class BaseElement {
  constructor({ element, taskId, timePositionSeconds, tag = 'div' }) {
    this.id = uuidv4()
    this.element = element
    this.taskId = taskId
    this.timePositionSeconds = timePositionSeconds
    this.tag = tag

    // calculate time properties
    const second = timePositionSeconds % 60
    const minute = Math.floor(timePositionSeconds / 3600)
    const hour = Math.floor((timePositionSeconds % 3600) / 60)

    // format to two digits
    this.timePositionSecond = second
    this.timePositionMinute = minute
    this.timePositionHour = hour

    // format
    this.timePosition = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:${String(second).padStart(2, '0')}`
  }
}

/**
 * TranscriptionElement class
 * - Represents a transcription, with text and speaker information.
 */
class TranscriptionElement extends BaseElement {
  constructor({
    // Base Properties
    element,
    taskId,
    timePositionSeconds,
    // Transcription Properties
    text = '',
    speaker = '-'
  }) {
    super({ element, taskId, timePositionSeconds })

    this.text = text
    this.speaker = speaker
  }
}

/**
 * AnnotationStartElement class
 * - Represents the start of an annotation with text.
 */
class AnnotationStartElement extends BaseElement {
  constructor({
    // Base Properties
    element,
    taskId,
    timePositionSeconds,
    creationTimeStamp = dayjs(new Date()).format('DD/MM/YYYY HH:mm:ss'),
    creatorId = useAuthStore().user.username,
    text = ''
  }) {
    super({
      element,
      taskId,
      timePositionSeconds
    })

    this.creationTimeStamp = creationTimeStamp
    this.annotationTitle = getElementTitle(element)
    this.creatorId = creatorId
    this.text = text
  }
}

/**
 * AnnotationEndElement class
 * - Represents the end of an annotation.
 */
class AnnotationEndElement extends BaseElement {
  constructor({
    // Base Properties
    element,
    taskId,
    timePositionSeconds,
    creationTimeStamp = dayjs(new Date()).format('DD/MM/YYYY HH:mm:ss'),
    creatorId = useAuthStore().user.username
  }) {
    super({
      element,
      taskId,
      timePositionSeconds
    })

    this.creationTimeStamp = creationTimeStamp
    this.annotationTitle = getElementTitle(element)
    this.creatorId = creatorId
  }
}

/**
 * CaseAnnotationElement class
 * - Represents case-related annotations, including boundaries and case-specific metadata.
 */
class CaseAnnotationElement extends BaseElement {
  constructor({
    // Base Properties
    element,
    taskId,
    timePositionSeconds,
    creationTimeStamp = dayjs(new Date()).format('DD/MM/YYYY HH:mm:ss'),
    creatorId = useAuthStore().user.username,
    // Case Properties
    caseUid,
    caseSerialNumber,
    caseInsertType,
    caseInsertTypeTitle,
    caseEndType = null,
    caseEndTypeTitle = null,
    claimants = [],
    defendants = [],
    abm = null,
    gad = null,
    gak = null,
    subDocketCode = null,
    extraInfo = null
  }) {
    super({ element, taskId, timePositionSeconds })

    this.creationTimeStamp = creationTimeStamp
    this.annotationTitle = getElementTitle(element)
    this.caseUid = caseUid
    this.caseSerialNumber = caseSerialNumber
    this.caseInsertType = caseInsertType
    this.caseInsertTypeTitle = caseInsertTypeTitle
    this.claimants = claimants
    this.defendants = defendants
    this.abm = abm
    this.gad = gad
    this.gak = gak
    this.creatorId = creatorId
    this.caseEndType = caseEndType
    this.caseEndTypeTitle = caseEndTypeTitle
    this.subDocketCode = subDocketCode
    this.extraInfo = extraInfo
  }
}

/**
 * Element options
 * - Defines titles and colors for specific element types.
 */
const elementOptions = {
  paragraph: {
    text: '',
    color: ''
  },
  mergePoint: {
    text: '',
    color: 'red'
  },
  task: {
    text: '',
    color: 'white'
  },
  StartBorder: {
    text: 'Έναρξη Υπόθεσης',
    color: 'green'
  },
  EndBorder: {
    text: 'Λήξη Υπόθεσης',
    color: 'red'
  },
  StopBorder: {
    text: 'Διακοπή Υπόθεσης',
    color: 'yellow'
  },
  RestartBorder: {
    text: 'Επανέναρξη Υπόθεσης',
    color: 'yellowgreen'
  },
  BreakBorder: {
    text: 'Έναρξη Διαλείμματος',
    color: 'orange'
  },
  BreakBorderStop: {
    text: 'Λήξη Διαλείμματος',
    color: 'orange'
  },
  CommentBorder: {
    text: 'Σχόλιο Γραμματέα',
    color: 'gray'
  },
  CorrectionBorder: {
    text: 'Σχόλιο Διόρθωσης',
    color: 'darkslategray'
  },
  TestemonyBorder: {
    text: 'Έναρξη Μαρτυρίας',
    color: 'brown'
  },
  TestemonyBorderStop: {
    text: 'Λήξη Μαρτυρίας',
    color: 'brown'
  },
  ProsecutorSpeechBorder: {
    text: 'Έναρξη Εισαγγελέα',
    color: 'brown'
  },
  ProsecutorSpeechBorderStop: {
    text: 'Λήξη Εισαγγελέα',
    color: 'brown'
  },
  TreatmentBorder: {
    text: 'Έναρξη υπεράσπισης κατηγορίας',
    color: 'brown'
  },
  TreatmentBorderStop: {
    text: 'Λήξη υπεράσπισης κατηγορίας',
    color: 'brown'
  },
  DefenseBorder: {
    text: 'Έναρξη υπεράσπισης',
    color: 'brown'
  },
  DefenseBorderStop: {
    text: 'Λήξη υπεράσπισης',
    color: 'brown'
  },
  AccusedBorder: {
    text: 'Έναρξη κατηγορούμενου',
    color: 'brown'
  },
  AccusedBorderStop: {
    text: 'Λήξη κατηγορούμενου',
    color: 'brown'
  },
  ReadableBorder: {
    text: 'Έναρξη αναγνωστέων',
    color: '#00008b'
  },
  ReadableBorderStop: {
    text: 'Λήξη αναγνωστέων',
    color: '#00008b'
  }
}

/**
 * Creates an element based on its type and validates it.
 * @param {Object} elementData - Data to create the element.
 * @returns {Object} - Created and validated element.
 */
export const createElement = (elementData) => {
  let newElement
  switch (elementData.element) {
    case 'paragraph':
      newElement = new ParagraphElement(elementData)
      break
    case 'transcription':
      newElement = new TranscriptionElement(elementData)
      break
    case 'StartBorder':
    case 'RestartBorder':
    case 'StopBorder':
    case 'EndBorder':
      newElement = new CaseAnnotationElement(elementData)
      break
    case 'BreakBorder':
    case 'TestemonyBorder':
    case 'ProsecutorSpeechBorder':
    case 'TreatmentBorder':
    case 'DefenseBorder':
    case 'AccusedBorder':
    case 'ReadableBorder':
    case 'CommentBorder':
    case 'CorrectionBorder':
      newElement = new AnnotationStartElement(elementData)
      break
    case 'BreakBorderStop':
    case 'TestemonyBorderStop':
    case 'ProsecutorSpeechBorderStop':
    case 'TreatmentBorderStop':
    case 'DefenseBorderStop':
    case 'AccusedBorderStop':
    case 'ReadableBorderStop':
      newElement = new AnnotationEndElement(elementData)
      break
    default:
      throw new Error(`Unsupported element type: ${elementData.element}`)
  }

  try {
    validateJsonTextElement(newElement)
    return newElement
  } catch (error) {
    if (error instanceof z.ZodError) {
      // Zod provides detailed error information
      console.error('Validation errors:', error.errors)
    } else {
      console.error('Unexpected error:', error)
    }
  }
}

/**
 * Get the color associated with an element type.
 * @param {string} element - Element type.
 * @returns {string|null} - Associated color or null if not found.
 */
export const getElementColor = (element) => elementOptions[element]?.color || null

/**
 * Get the title associated with an element type.
 * @param {string} element - Element type.
 * @returns {string|null} - Associated title or null if not found.
 */

export const getElementTitle = (element) => elementOptions[element]?.text || null
